import type {PlausibleCartSource} from '@bitstillery/common/lib/utils'
import {classes, plausible_event} from '@bitstillery/common/lib/utils'
import {MithrilComponent} from '@bitstillery/mithril'
import {Button, FieldNumber} from '@bitstillery/common/components/portal'
import {copy_object, present} from '@bitstillery/common/lib/utils'
import m from '@bitstillery/mithril'
import type {CartItemLookup, CartItemState, CartOfferItem} from '@/models/cart'
import {CartUpdateAction} from '@/models/cart'

import {$m, $s} from '@/app'
import {DEFAULT_MAX_QUANTITY, quantity_registry, type QuantityContext} from '@/lib/quantity_registry'

export interface ProductQuantityAttrs {
    /** simple mode hides the quantity input */
    mode: 'simple' | 'full'
    offer_item: CartOfferItem
    /** Track the source of cart events */
    source: PlausibleCartSource
}

export class ProductQuantity extends MithrilComponent<ProductQuantityAttrs> {
    context: QuantityContext

    constructor(vnode: m.Vnode<ProductQuantityAttrs>) {
        super()
        // Get the context from the registry, because we want to share
        // Computed data and business logic between components.
        this.context = quantity_registry.get_or_create(vnode.attrs.offer_item.case_artkey, vnode.attrs.offer_item)
    }

    override onremove(vnode: m.Vnode<ProductQuantityAttrs>) {
        // Only clean up if item is NOT in the cart; e.g. on page change.
        // This prevents recreation when scrolling/navigating while item is in cart
        const cart_items: CartItemLookup = $s.cart.items
        if (!cart_items[vnode.attrs.offer_item.case_artkey]) {
            quantity_registry.delete(vnode.attrs.offer_item.case_artkey)
        }
    }

    async cart_add(vnode: m.Vnode<ProductQuantityAttrs>, units: number) {
        // Set model value to target before cart operation
        const target_units = (this.context.data._cart_units || 0) + units
        this.context.data._model_value = target_units

        const {case_artkey} = vnode.attrs.offer_item
        if (!(case_artkey in $s.cart.items)) {
            // State may be gone when removing from other widgets. Make
            // Sure adding items to the cart is done only through THIS widget.
            this.set_cart_item_state(vnode, target_units)
            // Recreate context now that item exists in cart - the old context
            // Had _cart_item computed tracking nothing (non-existent property)
            quantity_registry.delete(case_artkey)
            this.context = quantity_registry.get_or_create(case_artkey, vnode.attrs.offer_item)
        }

        const cart_update_status = await $m.cart.update_cart(
            {
                number_of_cases: this.context.data._model_value_cases,
                price_per_case: vnode.attrs.offer_item.list_price,
            },
            vnode.attrs.offer_item.case_artkey,
            true,
        )

        // Reset override so _model_value falls back to committed cart value
        this.context.sync_model_value()

        plausible_event(cart_update_status.action, {
            props: {
                alcohol: vnode.attrs.offer_item.bottle_alcohol_percentage,
                cases: cart_update_status.amount,
                company_type: $s.identity.user.company_type,
                customs_status: vnode.attrs.offer_item.case_customs_status,
                product_category: vnode.attrs.offer_item.product_category,
                product_name: vnode.attrs.offer_item.product_name,
                source: vnode.attrs.source,
                volume: vnode.attrs.offer_item.bottle_volume,
            },
            revenue: {
                amount: Math.round(Number(cart_update_status.amount) * Number(vnode.attrs.offer_item.list_price) * 100) / 100,
                currency: $s.identity.user.currency,
            },
        })

        $m.cart.reset_quantity_validation(vnode.attrs.offer_item.case_artkey)
    }

    async cart_remove(vnode: m.Vnode<ProductQuantityAttrs>, units: number) {
        // Set model value to target before cart operation
        const target_units = Math.max(0, (this.context.data._cart_units || 0) - units)
        // Use 0 explicitly instead of null to ensure override is set correctly
        // When target_units is 0, we want to set override to 0, not null
        this.context.data._model_value = target_units === 0 ? 0 : target_units

        const cart_update_status = await $m.cart.update_cart(
            {
                number_of_cases: this.context.data._model_value_cases,
                price_per_case: vnode.attrs.offer_item.list_price,
            },
            vnode.attrs.offer_item.case_artkey,
        )

        // Reset override so _model_value falls back to committed cart value
        this.context.sync_model_value()

        // After deletion, ensure computed properties update by accessing them
        // Check both the action and the state to handle cases where deletion is in progress
        // (update_cart sets number_of_cases to 0 synchronously, even if API call is debounced)
        const item_was_deleted =
            cart_update_status.action === CartUpdateAction.CART_REMOVE ||
            (this.context.data._model_value_cases === 0 && this.context.data._cart_units === 0)

        if (item_was_deleted) {
            // Access computed properties to ensure they update after deletion
            // This ensures reactivity tracks the deletion properly
            void this.context.data._cart_item
            void this.context.data._cart_units
        }

        $m.cart.reset_quantity_validation(vnode.attrs.offer_item.case_artkey)
        plausible_event(cart_update_status.action, {
            props: {
                alcohol: vnode.attrs.offer_item.bottle_alcohol_percentage,
                cases: cart_update_status.amount,
                company_type: $s.identity.user.company_type,
                customs_status: vnode.attrs.offer_item.case_customs_status,
                product_category: vnode.attrs.offer_item.product_category,
                product_name: vnode.attrs.offer_item.product_name,
                source: vnode.attrs.source,
                volume: vnode.attrs.offer_item.bottle_volume,
            },
            revenue: {
                // Round to 2 decimal places
                amount: Math.round(Number(cart_update_status.amount) * Number(vnode.attrs.offer_item.list_price) * 100) / 100,
                currency: $s.identity.user.currency,
            },
        })
    }

    set_cart_item_state(vnode: m.Vnode<ProductQuantityAttrs>, value: number) {
        const {
            bottle_alcohol_percentage,
            bottle_refill_status,
            bottle_volume,
            case_artkey,
            case_customs_status,
            case_gift_box_type,
            case_number_of_bottles,
            custom_offer_item_artkey,
            excise_per_case,
            list_price,
            artkey: offer_item_artkey,
            offer_item_type,
            product_name,
            product_category,
            vat_percentage,
        } = copy_object(vnode.attrs.offer_item)

        const cart_item_state: CartItemState = {
            artkey: null,
            bottle_alcohol_percentage,
            bottle_refill_status,
            bottle_volume,
            case_artkey,
            case_customs_status,
            case_number_of_bottles,
            list_price,
            // A new line: add_cart_item_state derives the cases from `value`.
            number_of_cases: 0,
            offer_item_artkey,
            offer_item_type,
            product_category,
            product_name,
            unit: $s.identity.user.price_preference,
            value,
            // Optional on the offer item; a line loaded with the order lacks them the same way.
            ...(case_gift_box_type === undefined ? {} : {case_gift_box_type}),
            ...(custom_offer_item_artkey === undefined ? {} : {custom_offer_item_artkey}),
            ...(excise_per_case === undefined ? {} : {excise_per_case}),
            ...(vat_percentage === undefined ? {} : {vat_percentage}),
        }

        $m.cart.add_cart_item_state(cart_item_state)
    }

    view(vnode: m.Vnode<ProductQuantityAttrs>) {
        return (
            <div
                className={classes('c-product-quantity', {
                    simple: vnode.attrs.mode === 'simple',
                    'price-invalid': this.context.data._price_invalid_cart,
                    'quantity-invalid': this.context.data._quantity_invalid,
                })}
            >
                <div className='widget'>
                    <div className={classes('actions-extended', {active: this.context.data._cart_units})}>
                        {this.context.data.left._active && (
                            <Button
                                className='btn-quantity btn-left'
                                disabled={this.context.data.left._disabled}
                                // Simple mode (the cart pill) has a dedicated trash button, so the minus
                                // stays a minus instead of morphing into a second trash at the low end.
                                icon={
                                    vnode.attrs.mode === 'simple'
                                        ? 'minus'
                                        : present(this.context.data.left._state, 'left button state').icon
                                }
                                onclick={async () => {
                                    this.context.data.loading = true
                                    if (this.context.data._price_invalid_cart || this.context.data._quantity_invalid) {
                                        await this.cart_remove(vnode, this.context.data._cart_units)
                                    } else if (this.context.data._model_value === this.context.data._cart_units) {
                                        const prev_valid_units = this.context.find_prev_valid_units(
                                            this.context.data._model_value,
                                        )
                                        await this.cart_remove(vnode, this.context.data._model_value - prev_valid_units)
                                    } else {
                                        await this.cart_remove(
                                            vnode,
                                            this.context.data._cart_units - (this.context.data._model_value ?? 0),
                                        )
                                    }
                                    this.context.data.loading = false
                                }}
                                size={vnode.attrs.mode === 'simple' ? 's' : undefined}
                                tip={() => present(this.context.data.left._state, 'left button state').tip}
                                type={present(this.context.data.left._state, 'left button state').type}
                                variant='toggle'
                            />
                        )}
                        {vnode.attrs.mode !== 'simple' && (
                            <FieldNumber
                                className='input-amount'
                                disabled={this.context.data._price_invalid_cart}
                                min={0}
                                max={((): number => {
                                    if (this.context.data._stock_units !== null) {
                                        return this.context.data._stock_units
                                    }
                                    if ($s.cart.show_bottles) {
                                        return DEFAULT_MAX_QUANTITY * vnode.attrs.offer_item.case_number_of_bottles
                                    }
                                    return DEFAULT_MAX_QUANTITY
                                })()}
                                model={[this.context.data, '_model_value']}
                                onarrowdown={(_e, model_value) => {
                                    const prev_valid_quantity = this.context.find_prev_valid_units(model_value ?? 0)
                                    this.context.data._model_value = prev_valid_quantity
                                }}
                                onarrowup={(_e, model_value) => {
                                    const next_valid_quantity = this.context.find_next_valid_units(model_value ?? 0)
                                    this.context.data._model_value = next_valid_quantity
                                }}
                                onenter={(_e, _model_value) => {
                                    // Don't commit if model_value has validation errors
                                    if (
                                        this.context.data._min_quantity_invalid_modelvalue ||
                                        this.context.data._step_size_quantity_invalid_modelvalue
                                    ) {
                                        return
                                    }

                                    // Commit the model_value to cart
                                    const add_amount = Math.max(
                                        0,
                                        (this.context.data._model_value ?? 0) - this.context.data._cart_units,
                                    )
                                    const remove_amount = Math.max(
                                        0,
                                        this.context.data._cart_units - (this.context.data._model_value ?? 0),
                                    )

                                    if (add_amount > 0) {
                                        const stock_units = this.context.data._stock_units
                                        if (stock_units === null || this.context.data._cart_units + add_amount <= stock_units) {
                                            void this.cart_add(vnode, add_amount)
                                        }
                                    } else if (remove_amount > 0) {
                                        void this.cart_remove(vnode, remove_amount)
                                    }
                                }}
                                type='number'
                            />
                        )}
                    </div>
                    {this.context.data.right._active && (
                        <Button
                            className='btn-quantity btn-right'
                            disabled={this.context.data.right._disabled}
                            icon={present(this.context.data.right._state, 'right button state').icon}
                            onclick={async () => {
                                this.context.data.loading = true
                                if (this.context.data._price_invalid_cart) {
                                    void $m.cart.confirm_suggestion_price_up(vnode.attrs.offer_item.case_artkey)
                                } else if (this.context.data._quantity_invalid) {
                                    void $m.cart.confirm_suggestion_quantity(vnode.attrs.offer_item.case_artkey)
                                } else {
                                    let next_valid_quantity
                                    if (
                                        !this.context.data._model_value ||
                                        this.context.data._model_value === this.context.data._cart_units
                                    ) {
                                        // There was no fiddling with the input, so find the next valid quantity.
                                        next_valid_quantity = this.context.find_next_valid_units(
                                            this.context.data._model_value ?? 0,
                                        )
                                    } else {
                                        next_valid_quantity = this.context.data._model_value
                                    }
                                    const add_amount = Math.max(0, next_valid_quantity - this.context.data._cart_units)
                                    await this.cart_add(vnode, add_amount)
                                }

                                this.context.data.loading = false
                            }}
                            size={vnode.attrs.mode === 'simple' ? 's' : undefined}
                            tip={() => present(this.context.data.right._state, 'right button state').tip}
                            type={present(this.context.data.right._state, 'right button state').type}
                            variant='toggle'
                        />
                    )}
                </div>
            </div>
        )
    }
}
